"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, Bell, BookCheck, Code, RefreshCw, Download } from "lucide-react";
import client from "@/lib/api/client";
import { _ } from "@/lib/api/i18n";
import { mapValues } from "@/lib/api/templating";
import { useLayout } from "@/lib/context/layout";
import { toast } from "sonner";

interface Task {
    id: string;
    name: string;
    state: string;
    start_at?: string;
    message?: string;
    notification?: string;
    type?: string;
    timestamp?: string;
}

interface TaskStep {
    id: string;
    description: string;
    start_at: string;
    ended_at: string | null;
    params: string;
    response: string | null;
}

interface TaskState {
    error: Task[];
    success: Task[];
    running: Task[];
    notifications: Task[];
    notify: boolean;
}

const obj_to_html = (obj: unknown): React.ReactNode => {
    if (typeof obj === "string") return obj;
    if (typeof obj === "boolean") return String(obj);
    if (Array.isArray(obj)) {
        return (
            <ul>
                {obj.map((ob, i) => (
                    <li key={i}>{obj_to_html(ob)}</li>
                ))}
            </ul>
        );
    }
    if (obj instanceof Object) {
        return (
            <ul className="my-0">
                {Object.keys(obj).map((key) => (
                    <li key={key}>
                        <b className="font-medium">{key}:</b>
                        <i className="text-sm"> {obj_to_html((obj as Record<string, unknown>)[key])}</i>
                    </li>
                ))}
            </ul>
        );
    }
    return String(obj);
};

const StepElement = ({ task }: { task: Task }) => {
    const [steps, setSteps] = useState<TaskStep[]>([]);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!task.message) {
            client.get_task_steps(task.id).then((resp: TaskStep[]) => {
                if (resp) {
                    setSteps(resp.sort((a, b) => Number(a.id) - Number(b.id)));
                    intervalRef.current = setInterval(() => {
                        client.get_task_steps(task.id).then((r: TaskStep[]) => {
                            if (r) setSteps(r.sort((a, b) => Number(a.id) - Number(b.id)));
                        });
                    }, 4000);
                }
            });
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [task.id]);

    if (task.message) {
        return <div>{task.message}</div>;
    }

    return (
        <div>
            {steps.map((step, idx) => {
                let step_params: Record<string, unknown> = {};
                try {
                    step_params = JSON.parse(step.params);
                } catch {
                    step_params = { raw: step.params };
                }
                let response: React.ReactNode = <br />;
                if (step.response) {
                    try {
                        response = obj_to_html(JSON.parse(step.response));
                    } catch {
                        response = step.response;
                    }
                }
                return (
                    <div key={step.id}>
                        <p className="mb-0">
                            <b className="font-medium flex items-center gap-1">
                                {!step.ended_at && task.state === "error" && (
                                    <Badge variant="destructive">Error</Badge>
                                )}
                                {!step.ended_at && task.state === "running" && (
                                    <Badge variant="secondary">Running</Badge>
                                )}
                                {step.ended_at && <Badge variant="default">Success</Badge>}
                                <i> {step.description}</i>
                            </b>
                            <br />
                            <b className="font-medium">{_("Start")}:</b>
                            <i className="text-sm"> {step.start_at}</i>
                            <br />
                            <b className="font-medium">{_("End")}:</b>
                            <i className="text-sm"> {step.ended_at}</i>
                        </p>
                        <b className="font-medium">{_("Parameters")}: </b>
                        <ul className="mb-2 mt-0">
                            {Object.keys(step_params).map((key) => (
                                <li key={key}>
                                    <b className="font-medium">{key}:</b>
                                    <i className="text-sm"> {String(step_params[key])}</i>
                                </li>
                            ))}
                        </ul>
                        <b className="font-medium">{_("Response")}:</b>
                        <i className="text-sm"> {response}</i>
                        {idx < steps.length - 1 && <hr className="my-2 border-border" />}
                    </div>
                );
            })}
        </div>
    );
};

const RefreshModal = ({
    visible,
    onClose,
}: {
    visible: { visible: boolean; task: Task | null };
    onClose: () => void;
}) => {
    const [logs, setLogs] = useState<string[]>([]);
    const logEndRef = useRef<HTMLParagraphElement>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (visible.visible && visible.task) {
            const fetchLogs = () => {
                client.get_task_logs(visible.task!.id).then((_logs: string[]) => {
                    if (_logs.includes("err")) return;
                    setLogs((prev) => {
                        if (prev.length === _logs.length) return prev;
                        setTimeout(() => {
                            logEndRef.current?.scrollIntoView({ behavior: "smooth" });
                        }, 300);
                        return _logs;
                    });
                });
            };
            fetchLogs();
            intervalRef.current = setInterval(fetchLogs, 5000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [visible]);

    return (
        <Dialog
            open={visible.visible}
            onOpenChange={(open) => {
                if (!open) {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    setLogs([]);
                    onClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>LOGS</DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-96">
                    {logs.map((log, index) => (
                        <p key={index} className="m-0" ref={index === logs.length - 1 ? logEndRef : undefined}>
                            {log}
                        </p>
                    ))}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export const TasksElement = () => {
    const [state, setState] = useState<TaskState>({
        error: [],
        success: [],
        running: [],
        notifications: [],
        notify: false,
    });
    const [logsVisible, setLogsVisible] = useState<{ visible: boolean; task: Task | null }>({
        visible: false,
        task: null,
    });
    const { editEnabled } = useLayout();
    const stateRef = useRef(state);
    stateRef.current = state;

    const fetch_tasks = (task_interval?: ReturnType<typeof setInterval>) => {
        if (editEnabled) {
            if (task_interval) clearInterval(task_interval);
            return;
        }
        client.get_tasks().then((resp: { tasks?: Task[]; notification?: Task[] } | undefined) => {
            if (resp === undefined) return;
            const new_state: TaskState = {
                error: [],
                success: [],
                running: [],
                notify: true,
                notifications: [],
            };
            const prev = stateRef.current;

            for (const notification of resp.notification || []) {
                notification.message = JSON.parse(notification.notification || "{}");
                new_state.notifications.push(notification);
                if (!prev.notify) continue;
                if (!prev.notifications.find((k) => k.id === notification.id)) {
                    if (notification.type === "success") {
                        toast.success(_("Success"), { description: _("Check yours notifications.") });
                    }
                    if (notification.type === "error") {
                        toast.error(_("Error"), { description: _("Check yours notifications.") });
                    }
                }
            }

            for (const task of resp.tasks || []) {
                if (task.state === "finished") {
                    new_state.success.push(task);
                    if (!prev.notify) continue;
                    if (!prev.success.find((k) => k.id === task.id)) {
                        toast.success(_("Success"), {
                            description: mapValues(_("Task #name# finished with success!"), task as unknown as Record<string, unknown>) as string,
                        });
                    }
                }
                if (task.state === "running") {
                    new_state.running.push(task);
                    if (!prev.notify) continue;
                    if (!prev.running.find((k) => k.id === task.id)) {
                        toast.info(_("Info"), {
                            description: mapValues(_("Task #name# is running!"), task as unknown as Record<string, unknown>) as string,
                        });
                    }
                }
                if (task.state === "error") {
                    new_state.error.push(task);
                    if (!prev.notify) continue;
                    if (!prev.error.find((k) => k.id === task.id)) {
                        toast.error(_("Error"), {
                            description: mapValues(_("Error running Task #name# !"), task as unknown as Record<string, unknown>) as string,
                        });
                    }
                }
            }
            setState(new_state);
        });
    };

    useEffect(() => {
        fetch_tasks();
        const task_interval = setInterval(() => {
            fetch_tasks(task_interval);
        }, 10000);
        return () => clearInterval(task_interval);
    }, []);

    const total = state.success.length + state.running.length + state.error.length;
    const notifications_total = state.notifications.length;

    const rerun = (task: Task) => {
        client.run_task(task.id).then(() => fetch_tasks());
    };

    const download = (task: Task) => {
        client.get_task_steps(task.id).then((resp: TaskStep[]) => {
            const _task = { ...task, steps: resp };
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(_task));
            const downloadAnchorNode = document.createElement("a");
            downloadAnchorNode.setAttribute("href", dataStr);
            downloadAnchorNode.setAttribute("download", task.id + ".json");
            document.body.appendChild(downloadAnchorNode);
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
        });
    };

    const read = (task: Task) => {
        if (task.message) {
            return client.read_notification(task.id).then(() => fetch_tasks());
        }
        client.read_task(task.id).then(() => fetch_tasks());
    };

    const renderTaskList = (tasks: Task[], showRerun = false) => (
        <Accordion multiple className="w-full">
            {tasks.map((task) => (
                <AccordionItem key={task.id} value={task.id}>
                    <AccordionTrigger className="hover:no-underline">
                        <span className="flex items-center gap-2 w-full text-left">
                            <span className="font-bold whitespace-nowrap flex-1 truncate">
                                {task.name
                                    ? task.name
                                    : task.timestamp + " - " + (task.message?.[0]?.slice(0, 30) || "") +
                                      ((task.message?.[0]?.length || 0) > 30 ? "..." : "")}
                            </span>
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => {
                                        read(task);
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <BookCheck className="h-3.5 w-3.5" />
                                </Button>
                                {showRerun && task.state === "error" && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-destructive"
                                            onClick={(e) => {
                                                rerun(task);
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        >
                                            <RefreshCw className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={(e) => {
                                                download(task);
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                        </Button>
                                    </>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => {
                                        setLogsVisible({ visible: true, task });
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <Code className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </span>
                    </AccordionTrigger>
                    <AccordionContent>
                        <StepElement task={task} />
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    );

    return (
        <>
            <Popover>
                <PopoverTrigger
                    className="relative inline-flex items-center justify-center h-10 w-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
                >
                    <BookOpen className="h-5 w-5" />
                    {total !== 0 && (
                        <Badge className="absolute -top-0 -right-0 h-4 min-w-4 px-1 text-xs">
                            {total}
                        </Badge>
                    )}
                </PopoverTrigger>
                <PopoverContent className="w-96 p-0" align="end">
                    <Tabs defaultValue="success">
                        <TabsList className="w-full justify-around">
                            <TabsTrigger value="success" className="flex-1">
                                {_("Success")}
                                <Badge variant="default" className="ml-1">{state.success.length}</Badge>
                            </TabsTrigger>
                            <TabsTrigger value="running" className="flex-1">
                                {_("Running")}
                                <Badge variant="secondary" className="ml-1">{state.running.length}</Badge>
                            </TabsTrigger>
                            <TabsTrigger value="error" className="flex-1">
                                {_("Errors")}
                                <Badge variant="destructive" className="ml-1">{state.error.length}</Badge>
                            </TabsTrigger>
                        </TabsList>
                        <ScrollArea className="h-80">
                            <TabsContent value="success" className="mt-0 p-2">
                                {state.success.length > 0 && (
                                    <div className="flex justify-end p-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => state.success.map((t) => read(t))}
                                        >
                                            <BookCheck className="h-3.5 w-3.5 mr-1" />
                                            {_("Mark all as read")}
                                        </Button>
                                    </div>
                                )}
                                {renderTaskList(state.success)}
                            </TabsContent>
                            <TabsContent value="running" className="mt-0 p-2">
                                {state.running.length > 0 && (
                                    <div className="flex justify-end p-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => state.running.map((t) => read(t))}
                                        >
                                            <BookCheck className="h-3.5 w-3.5 mr-1" />
                                            {_("Mark all as read")}
                                        </Button>
                                    </div>
                                )}
                                {renderTaskList(state.running)}
                            </TabsContent>
                            <TabsContent value="error" className="mt-0 p-2">
                                {state.error.length > 0 && (
                                    <div className="flex justify-end p-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => state.error.map((t) => read(t))}
                                        >
                                            <BookCheck className="h-3.5 w-3.5 mr-1" />
                                            {_("Mark all as read")}
                                        </Button>
                                    </div>
                                )}
                                {renderTaskList(state.error, true)}
                            </TabsContent>
                        </ScrollArea>
                    </Tabs>
                </PopoverContent>
            </Popover>

            <Popover>
                <PopoverTrigger
                    className="relative inline-flex items-center justify-center h-10 w-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
                >
                    <Bell className="h-5 w-5" />
                    {notifications_total !== 0 && (
                        <Badge className="absolute -top-0 -right-0 h-4 min-w-4 px-1 text-xs">
                            {notifications_total}
                        </Badge>
                    )}
                </PopoverTrigger>
                <PopoverContent className="w-96 p-0" align="end">
                    {state.notifications.length > 0 && (
                        <div className="flex justify-end p-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => state.notifications.map((t) => read(t))}
                            >
                                <BookCheck className="h-3.5 w-3.5 mr-1" />
                                {_("Mark all as read")}
                            </Button>
                        </div>
                    )}
                    <ScrollArea className="h-80">
                        <div className="p-2">
                            {renderTaskList(state.notifications)}
                        </div>
                    </ScrollArea>
                </PopoverContent>
            </Popover>

            <RefreshModal visible={logsVisible} onClose={() => setLogsVisible({ visible: false, task: null })} />
        </>
    );
};
